import * as THREE from 'three';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { colored, vary, seededRandom, jitter } from '../../Style.js';
import { hashId } from './style.js';
import { NAVY, paint, bake, hexColor, flagGeometry, waveFlag } from './units-style.js';

const NEUTRAL_RING = 0x9a9486;
const DECK_HEIGHT = 1.8;

// A coastal cannon: a round stone bastion on a weedy rock, its parapet band
// and flag painted in the owner's colour, with a big iron gun on a timber
// carriage that turns to track targets. Unclaimed bastions fly a white flag
// and wear plain whitewash.
export class Turret {
  constructor(scene, turret) {
    this.scene = scene;
    this.turret = turret;
    this.meshGroup = new THREE.Group();
    this.headGroup = null; // Rotating part (carriage + gun)
    this.baseMesh = null;
    this.headMesh = null; // Painted shield on the carriage, glows while tracking
    this.gunMesh = null;
    this.rangeMesh = null;
    this.teamMaterial = null; // Parapet band and flag, repainted on capture
    this.flagMesh = null;
    this.targetRotation = 0;
    this.currentRotation = 0;
    this.recoil = 0;
    this.lastTime = null;
    this.rand = seededRandom(hashId(turret.id));
    this.create();
  }

  create() {
    const neutral = this.turret.ownerId === -1;

    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getPaintColor(),
      roughness: 0.85,
      flatShading: true,
      side: THREE.DoubleSide,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.12
    });

    // Rock, bastion drum and parapet (stationary)
    this.baseMesh = new THREE.Mesh(bake(this.buildBastion()), paint());
    this.baseMesh.castShadow = true;
    this.baseMesh.receiveShadow = true;
    this.meshGroup.add(this.baseMesh);

    // Painted band around the parapet and the flag, in the owner's colour
    const band = new THREE.Mesh(bake(this.buildBand()), this.teamMaterial);
    band.castShadow = true;
    this.meshGroup.add(band);
    this.flagMesh = new THREE.Mesh(flagGeometry(1.2, 0.75), this.teamMaterial);
    this.flagMesh.position.set(-1.25, DECK_HEIGHT + 2.55, -1.25);
    this.flagMesh.rotation.y = Math.PI * 0.75;
    this.flagMesh.castShadow = true;
    this.meshGroup.add(this.flagMesh);

    // Rotating head: turntable and carriage, gun aimed along +Z
    this.headGroup = new THREE.Group();
    this.headGroup.position.y = DECK_HEIGHT;
    this.meshGroup.add(this.headGroup);

    const carriage = new THREE.Mesh(bake(this.buildCarriage()), paint());
    carriage.castShadow = true;
    this.headGroup.add(carriage);

    // The gun recoils along its carriage when it fires
    this.gunMesh = new THREE.Mesh(bake(this.buildGun()), paint());
    this.gunMesh.castShadow = true;
    this.headGroup.add(this.gunMesh);

    // Painted shield on the front of the carriage
    const shieldMaterial = new THREE.MeshStandardMaterial({
      color: this.getPaintColor(),
      roughness: 0.8,
      flatShading: true,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.2
    });
    this.headMesh = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.7, 0.1), shieldMaterial);
    this.headMesh.position.set(0, 0.62, 0.62);
    this.headMesh.rotation.x = -0.2;
    this.headMesh.castShadow = true;
    this.headGroup.add(this.headMesh);

    // Range indicator (semi-transparent ring)
    const rangeGeometry = new THREE.RingGeometry(
      this.turret.claimRadius - 0.3,
      this.turret.claimRadius,
      32
    );
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Position the group
    this.meshGroup.position.set(
      this.turret.position.x,
      this.turret.position.y,
      this.turret.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildBastion() {
    const rand = this.rand;
    const parts = [
      // Weedy rock the bastion stands on, mostly under water
      colored(jitter(new THREE.CylinderGeometry(2.3, 2.7, 0.9, 9, 1), 0.25, rand), NAVY.rockDark, { y: -0.15 }),
      colored(jitter(new THREE.CylinderGeometry(2.5, 2.55, 0.12, 9, 1), 0.12, rand), NAVY.seaweed, { y: 0.12 }),
      // Battered stone drum
      colored(new THREE.CylinderGeometry(1.75, 2.05, 1.5, 12), NAVY.stoneDark, { y: 0.85 })
    ];
    // Coursed blocks around the drum
    for (let row = 0; row < 3; row++) {
      for (let i = 0; i < 12; i++) {
        const a = ((i + row * 0.5) / 12) * Math.PI * 2;
        const r = 1.98 - row * 0.12;
        parts.push(colored(new THREE.BoxGeometry(0.98, 0.44, 0.22), vary(NAVY.stone, rand, 0.14, 0.02), {
          x: Math.sin(a) * r, y: 0.33 + row * 0.48, z: Math.cos(a) * r, ry: a, rx: 0.12
        }));
      }
    }
    // Gun platform and crenellated parapet
    parts.push(colored(new THREE.CylinderGeometry(1.8, 1.8, 0.16, 12), NAVY.deckDark, { y: DECK_HEIGHT - 0.12 }));
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      parts.push(colored(new THREE.BoxGeometry(0.62, 0.42, 0.3), vary(NAVY.stoneLight, rand, 0.1), {
        x: Math.sin(a) * 1.72, y: DECK_HEIGHT + 0.36, z: Math.cos(a) * 1.72, ry: a
      }));
    }
    // Flagstaff with a brass ball
    parts.push(colored(new THREE.CylinderGeometry(0.05, 0.07, 2.7, 6), NAVY.woodDark, { x: -1.25, y: DECK_HEIGHT + 1.25, z: -1.25 }));
    parts.push(colored(new THREE.SphereGeometry(0.1, 6, 4), NAVY.brass, { x: -1.25, y: DECK_HEIGHT + 2.62, z: -1.25 }));
    // A few cannonballs stacked on the platform
    for (const [x, z, y] of [[1.0, -0.9, 0], [1.2, -0.75, 0], [1.08, -0.7, 0.16]]) {
      parts.push(colored(new THREE.SphereGeometry(0.12, 6, 4), NAVY.iron, { x, y: DECK_HEIGHT + 0.05 + y, z }));
    }
    return parts;
  }

  // Painted ring below the parapet and merlon caps, in the owner's colour
  buildBand() {
    const parts = [colored(new THREE.CylinderGeometry(1.86, 1.9, 0.4, 12, 1, true), 0xffffff, { y: DECK_HEIGHT - 0.12 })];
    // Painted caps on the merlons
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      parts.push(colored(new THREE.BoxGeometry(0.66, 0.08, 0.34), 0xffffff, {
        x: Math.sin(a) * 1.72, y: DECK_HEIGHT + 0.6, z: Math.cos(a) * 1.72, ry: a
      }));
    }
    return parts;
  }

  buildCarriage() {
    const parts = [
      colored(new THREE.CylinderGeometry(1.0, 1.05, 0.14, 10), NAVY.iron, { y: 0.07 }),
      colored(new THREE.BoxGeometry(1.1, 0.14, 1.6), NAVY.woodDark, { y: 0.2, z: -0.1 })
    ];
    // Stepped cheeks with iron trunnion caps
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.14, 0.5, 1.3), NAVY.wood, { x: sx * 0.4, y: 0.5, z: -0.15 }));
      parts.push(colored(new THREE.CylinderGeometry(0.1, 0.1, 0.16, 6), NAVY.iron, { x: sx * 0.42, y: 0.72, z: 0.0, rz: Math.PI / 2 }));
    }
    // Trucks (wheels)
    for (const sx of [-1, 1]) {
      for (const z of [-0.6, 0.35]) {
        parts.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.1, 8), NAVY.woodDark, { x: sx * 0.5, y: 0.25, z, rz: Math.PI / 2 }));
      }
    }
    return parts;
  }

  // Big iron gun along +Z, with reinforcing rings and a flared muzzle
  buildGun() {
    return [
      colored(new THREE.CylinderGeometry(0.2, 0.3, 2.1, 10), NAVY.iron, { y: 0.74, z: 0.55, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.31, 0.31, 0.12, 10), NAVY.hat, { y: 0.74, z: -0.2, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.26, 0.26, 0.1, 10), NAVY.hat, { y: 0.74, z: 0.5, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.26, 0.21, 0.16, 10), NAVY.brass, { y: 0.74, z: 1.6, rx: Math.PI / 2 }),
      colored(new THREE.SphereGeometry(0.16, 6, 4), NAVY.iron, { y: 0.74, z: -0.6 })
    ];
  }

  getColor() {
    if (this.turret.ownerId === -1) {
      return 0x888888; // Gray for unclaimed
    }
    return hexColor(PLAYER_COLORS[this.turret.ownerId]);
  }

  getPaintColor() {
    return this.turret.ownerId === -1 ? NAVY.neutralPaint : this.getColor();
  }

  getRingColor() {
    return this.turret.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(turretData, time, units = []) {
    const ownerChanged = !this.turret || this.turret.ownerId !== turretData.ownerId;
    const wasTracking = this.turret && this.turret.isTracking;
    this.turret = turretData;
    const neutral = this.turret.ownerId === -1;

    // Repaint if the owner changed
    if (ownerChanged) {
      const color = this.getColor();
      this.teamMaterial.color.setHex(this.getPaintColor());
      this.teamMaterial.emissive.setHex(color);
      this.teamMaterial.emissiveIntensity = neutral ? 0 : 0.12;
      this.headMesh.material.color.setHex(this.getPaintColor());
      this.headMesh.material.emissive.setHex(color);
      this.rangeMesh.material.color.setHex(this.getRingColor());
    }

    // Handle destroyed state
    if (this.turret.isDestroyed) {
      this.meshGroup.visible = false;
      return;
    }
    this.meshGroup.visible = true;
    waveFlag(this.flagMesh, time, 0.14);

    const dt = this.lastTime === null ? 0 : Math.min(Math.max(time - this.lastTime, 0), 0.1);
    this.lastTime = time;

    if (!neutral && this.headGroup) {
      // Find nearest enemy and rotate toward them
      const nearestEnemy = this.findNearestEnemy(units);
      if (nearestEnemy) {
        const dx = nearestEnemy.position.x - this.turret.position.x;
        const dz = nearestEnemy.position.z - this.turret.position.z;
        this.targetRotation = Math.atan2(dx, dz);
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.currentRotation;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.currentRotation += rotationDiff * 0.1;
      this.headGroup.rotation.y = this.currentRotation;

      // The gun kicks back when a lock completes, then runs out again
      if (wasTracking && !this.turret.isTracking && this.turret.trackingProgress !== undefined) this.recoil = 1;
      this.recoil = Math.max(0, this.recoil - dt * 2);

      if (this.turret.isTracking) {
        // Shield glows brighter as the gun lays on target, and the gun is drawn in to load
        const trackingPulse = Math.sin(time * 8) * 0.12;
        this.headMesh.material.emissiveIntensity = 0.2 + trackingPulse + this.turret.trackingProgress * 0.5;
        this.gunMesh.position.z = -this.turret.trackingProgress * 0.25;
      } else {
        this.headMesh.material.emissiveIntensity = 0.2;
        this.gunMesh.position.z = -0.35 * this.recoil * this.recoil;
      }
    } else if (neutral && this.headGroup) {
      // Unmanned guns drift slowly
      this.headGroup.rotation.y = Math.sin(time * 0.15) * 1.2;
      this.headMesh.material.emissiveIntensity = 0;
      this.gunMesh.position.z = 0;
    }

    // Pulse effect for unclaimed turrets
    if (neutral) {
      this.rangeMesh.material.opacity = 0.2 + Math.sin(time * 2) * 0.1;
    } else {
      this.rangeMesh.material.opacity = 0.3;
    }
  }

  // Find nearest enemy unit within attack range
  findNearestEnemy(units) {
    if (!units || units.length === 0) return null;

    const attackRange = 20; // Match server TurretAttackRange (~4 squares)
    let nearest = null;
    let nearestDist = attackRange + 1;

    for (const unit of units) {
      // Only target enemy units
      if (unit.ownerId === this.turret.ownerId) continue;
      // Skip dead/respawning units
      if (unit.isRespawning) continue;

      const dx = unit.position.x - this.turret.position.x;
      const dz = unit.position.z - this.turret.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = unit;
      }
    }

    return nearest;
  }

  // Check if player is in claiming range
  isInRange(position) {
    const dx = position.x - this.turret.position.x;
    const dz = position.z - this.turret.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.turret.claimRadius * this.turret.claimRadius;
  }

  // Check if turret can be claimed by player
  canBeClaimed(playerId) {
    if (this.turret.isDestroyed) return false;
    if (this.turret.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    this.meshGroup.traverse(child => {
      if (child.isMesh) child.geometry.dispose();
    });
    // The vertex-colour material is shared; the paint, shield and ring materials are ours
    this.teamMaterial.dispose();
    this.headMesh.material.dispose();
    this.rangeMesh.material.dispose();
  }
}
